import type { Mutator } from 'final-form';
import React, { memo } from 'react';
import type { FormRenderProps } from 'react-final-form';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import { ExtraWalletsInfoBar } from '@swf/sports/components/ExtraWalletsInfoBar/ExtraWalletsInfoBar';

import useBetSlipMaxHeight from '../../hooks/useBetSlipMaxHeight';
import useCloseOnEmpty from '../../hooks/useCloseOnEmpty';
import useDismissibleBetSlipError from '../../hooks/useDismissibleBetSlipError';
import useOpportunities from '../../hooks/useOpportunities';
import useParseStakesDecorator from '../../hooks/useParseStakesDecorator';
import usePlaceBet from '../../hooks/usePlaceBet';
import type { FormData } from '../../types';
import { betSlipMutators } from '../../utils/betSlipFormMutators';
import CollapsibleLayout from '../CollapsibleLayout/CollapsibleLayout';
import Footer from '../Footer/Footer';

import { TXTWCL_Your_bet_slip_is_empty } from './BetSlip.messages.json';
import styles from './BetSlip.scss';
import BetSlipHeader from './components/BetSlipHeader/BetSlipHeader';
import MultipleOpportunities from './components/Opportunities/MultipleOpportunities';
import SingleOpportunities from './components/Opportunities/SingleOpportunities';
import SportsRulesLink from './components/SportsRulesLink/SportsRulesLink';
import StaticMessages from './components/StaticMessages/StaticMessages';

const BetSlip: React.FC<FormRenderProps<FormData>> = ({ handleSubmit }) => {
  const selectionsCount = useSelector(select.betSlip.selectionsCount);
  const singlesCount = useSelector(
    (state: RootState) => select.betSlip.singleOpportunities(state).length
  );
  const { contentTranslationTag } = useDismissibleBetSlipError();

  useCloseOnEmpty();
  useOpportunities();

  const { betSlipRef } = useBetSlipMaxHeight<HTMLFormElement>();

  return (
    <CollapsibleLayout
      as="form"
      onSubmit={handleSubmit}
      ref={betSlipRef}
      header={<BetSlipHeader />}
      showMobileHeader={selectionsCount > 0 || !!contentTranslationTag}
      main={
        <div className={styles.betSlip}>
          <div className={styles.content}>
            <ExtraWalletsInfoBar />
            <StaticMessages />
            {selectionsCount ? (
              <>
                <MultipleOpportunities />
                <SingleOpportunities />
                <SportsRulesLink />
              </>
            ) : (
              <div className={styles.emptyBetSlip}>
                <Translation {...TXTWCL_Your_bet_slip_is_empty} />
              </div>
            )}
          </div>
          <Footer showTotals={!!singlesCount} />
        </div>
      }
    />
  );
};

export default memo(() => {
  const placeBet = usePlaceBet();
  const parseStakes = useParseStakesDecorator();

  return (
    <Form<FormData>
      decorators={[parseStakes]}
      destroyOnUnregister
      initialValues={{}}
      onSubmit={placeBet}
      mutators={betSlipMutators as { [key: string]: Mutator<FormData> }}
      render={(formRenderProps) => <BetSlip {...formRenderProps} />}
    />
  );
});
